import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff, Lock, ArrowLeft, Shield } from 'lucide-react';
import { supabase } from '../../lib/supabase';

const UpdatePassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let subscription = null;

    const checkSession = async () => {
      try {
        // First check if there's already a session (e.g. from hash token processed by Supabase)
        const { data: { session }, error: sessionErr } = await supabase?.auth?.getSession();

        if (sessionErr) {
          setSessionError('Lien invalide ou expiré. Veuillez demander un nouveau lien de réinitialisation.');
          setCheckingSession(false);
          return;
        }

        if (session?.user) {
          setSessionReady(true);
          await detectUserRole(session?.user);
          setCheckingSession(false);
          return;
        }

        // Listen for PASSWORD_RECOVERY event (Supabase processes the hash token)
        const { data: authListener } = supabase?.auth?.onAuthStateChange(async (event, sess) => {
          console.log('[UPDATE-PASSWORD] Auth event:', event, sess?.user?.id);
          if ((event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') && sess?.user) {
            setSessionReady(true);
            await detectUserRole(sess?.user);
            setCheckingSession(false);
            subscription?.unsubscribe();
          }
        });

        subscription = authListener?.subscription;

        // Timeout fallback after 8 seconds
        setTimeout(() => {
          setCheckingSession(false);
          if (!sessionReady) {
            setSessionError('Lien invalide ou expiré. Veuillez demander un nouveau lien de réinitialisation.');
          }
        }, 8000);
      } catch (err) {
        console.error('[UPDATE-PASSWORD] Session check error:', err);
        setSessionError('Erreur lors de la vérification du lien. Veuillez réessayer.');
        setCheckingSession(false);
      }
    };

    checkSession();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const detectUserRole = async (user) => {
    try {
      // Check user_profiles first
      const { data: profile } = await supabase
        ?.from('user_profiles')
        ?.select('role')
        ?.eq('id', user?.id)
        ?.single();

      if (profile?.role) {
        setUserRole(profile?.role);
        return;
      }

      // Check employees table
      const { data: employee } = await supabase
        ?.from('employees')
        ?.select('role')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (employee?.role) {
        setUserRole(employee?.role);
        return;
      }

      // Check by email in employees
      const { data: empByEmail } = await supabase
        ?.from('employees')
        ?.select('role')
        ?.eq('email', user?.email?.toLowerCase())
        ?.single();

      if (empByEmail?.role) {
        setUserRole(empByEmail?.role);
      }
    } catch (err) {
      console.warn('[UPDATE-PASSWORD] Could not detect role:', err?.message);
    }
  };

  const getRedirectPath = () => {
    if (userRole === 'chauffeur') return '/driver-login';
    if (userRole === 'admin' || userRole === 'super_admin') return '/admin-login';
    if (userRole === 'agent_abidjan') return '/agent-abidjan-login';
    return '/admin-login';
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    if (!newPassword) {
      setError('Veuillez saisir un nouveau mot de passe.');
      return;
    }
    if (newPassword?.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (!confirmPassword) {
      setError('Veuillez confirmer votre nouveau mot de passe.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase?.auth?.updateUser({
        password: newPassword,
      });

      if (updateError) {
        throw updateError;
      }

      // Also update must_change_password if applicable
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase
          ?.from('employees')
          ?.update({ must_change_password: false })
          ?.eq('auth_user_id', user?.id);
      }

      setSuccess(true);

      // Sign out all other sessions for security
      try {
        await supabase?.auth?.signOut({ scope: 'others' });
      } catch (_) { /* ignore */ }

      const redirectPath = getRedirectPath();
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 3000);
    } catch (err) {
      console.error('[UPDATE-PASSWORD] Update error:', err);
      if (err?.message?.includes('expired') || err?.message?.includes('invalid') || err?.message?.includes('Auth session missing')) {
        setError('Le lien de réinitialisation est expiré ou invalide. Veuillez en demander un nouveau.');
      } else {
        setError(err?.message || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const redirectPath = getRedirectPath();

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mr-3">
              <span className="text-white font-bold text-lg">ADT</span>
            </div>
            <span className="text-xl font-bold text-gray-900">ADT Express</span>
          </div>
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock size={28} className="text-green-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Créer un nouveau mot de passe
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            Choisissez un mot de passe sécurisé d'au moins 8 caractères.
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl shadow-md p-6 sm:p-8">

          {/* Loading state */}
          {checkingSession && !sessionError && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <span className="ml-3 text-sm text-gray-600">Vérification du lien de récupération...</span>
            </div>
          )}

          {/* Session error */}
          {sessionError && !sessionReady && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800 mb-1">Lien invalide ou expiré</p>
                  <p className="text-sm text-red-700">{sessionError}</p>
                </div>
              </div>
              <Link
                to="/password-reset"
                className="block w-full text-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
              >
                Demander un nouveau lien
              </Link>
              <Link
                to="/driver-login"
                className="block w-full text-center px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
              >
                Retour à la connexion chauffeur
              </Link>
            </div>
          )}

          {/* Success */}
          {success && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
                <CheckCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-800 mb-1">Mot de passe modifié avec succès !</p>
                  <p className="text-sm text-green-700">
                    Votre nouveau mot de passe a été enregistré. Vous allez être redirigé vers la page de connexion...
                  </p>
                </div>
              </div>
              <Link
                to={redirectPath}
                className="block w-full text-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
              >
                Aller à la connexion
              </Link>
            </div>
          )}

          {/* Form */}
          {!success && !sessionError && !checkingSession && sessionReady && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-3">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Security notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                <Shield size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  Minimum 8 caractères. Utilisez un mélange de lettres, chiffres et symboles pour plus de sécurité.
                </p>
              </div>

              {/* New password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e?.target?.value)}
                    placeholder="Minimum 8 caractères"
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm"
                    disabled={loading}
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew(!showNew)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {newPassword && (
                  <div className="mt-1 flex gap-1">
                    {[...Array(4)]?.map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          newPassword?.length >= (i + 1) * 2
                            ? newPassword?.length >= 12 ? 'bg-green-500' : newPassword?.length >= 8 ? 'bg-yellow-500' : 'bg-red-400' :'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirmer le mot de passe
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    placeholder="Répétez le mot de passe"
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-gray-900 placeholder:text-gray-400 text-sm"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {confirmPassword && (
                  <p className={`text-xs mt-1 ${newPassword === confirmPassword ? 'text-green-600' : 'text-red-500'}`}>
                    {newPassword === confirmPassword ? '✓ Les mots de passe correspondent' : '✗ Les mots de passe ne correspondent pas'}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || newPassword !== confirmPassword || newPassword?.length < 8}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm min-h-[44px]"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Modification en cours...</span>
                  </>
                ) : (
                  <span>Valider le nouveau mot de passe</span>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Back link */}
        {!success && (
          <div className="mt-6 text-center">
            <Link
              to={redirectPath}
              className="inline-flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 font-medium min-h-[44px] py-2"
            >
              <ArrowLeft size={16} />
              <span>Retour à la connexion</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdatePassword;
